import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { LanguageSelector } from '../components/LanguageSelector';

export function LoginPage() {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [showForgotModal, setShowForgotModal] = useState(false);
  const { t } = useTranslation();

  const isNonProd = !window.location.hostname.startsWith('artifact.compliance.amazon');

  useEffect(() => {
    const err = searchParams.get('error');
    const reason = searchParams.get('reason');
    if (err) setError(decodeURIComponent(err));
    if (reason === 'inactivity') setError(t('login.inactivityLogout'));
  }, [searchParams, t]);

  function handleExternalLogin(e: React.FormEvent) {
    e.preventDefault();
    const hint = email.trim();
    const url = hint
      ? `/auth/idprism/login?login_hint=${encodeURIComponent(hint)}`
      : '/auth/idprism/login';
    window.location.href = url;
  }

  return (
    <div className="login-page">
      {isNonProd && (
        <div className="env-banner env-banner-fixed">
          NON-PRODUCTION ENVIRONMENT — For testing only
        </div>
      )}
      <div className="login-card">
        <div className="login-brand">
          <img src="/amazon-logo.png" alt="Amazon" className="amazon-logo" />
          <h1>{t('login.title')}</h1>
        </div>

        <h3 className="section-heading" style={{ textAlign: 'center' }}>{t('login.externalHeading')}</h3>

        {error && (
          <div className="form-error" role="alert">
            <p>{error}</p>
            <p style={{ marginTop: '0.5rem', fontSize: '0.9em' }}>
              {t('login.errorHint', { defaultValue: "Don't have an account yet?" })}{' '}
              <Link to="/signup" style={{ fontWeight: 600 }}>
                {t('login.signUpLink')}
              </Link>
            </p>
          </div>
        )}

        <form onSubmit={handleExternalLogin} className="login-form">
          <label htmlFor="login-email" className="login-label">{t('common.email')}</label>
          <input
            id="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className="login-input"
            autoFocus
          />
          <button type="submit" className="btn-primary login-continue-btn">
            {t('common.continue')}
          </button>
        </form>

        <div className="login-links" style={{ textAlign: 'center' }}>
          <Link to="/signup" className="btn-secondary" style={{ display: 'block', width: '100%', marginBottom: '0.5rem' }}>
            {t('login.signUpLink')}
          </Link>
          <button
            type="button"
            onClick={() => setShowForgotModal(true)}
            className="link-button"
          >
            {t('login.forgotPassword')}
          </button>
        </div>

        <div className="login-divider">
          <span>{t('login.employeeDivider')}</span>
        </div>

        <a href="/auth/midway/login" className="btn-midway">
          {t('login.midwayButton')}
        </a>

        <div className="login-footer">
          <p className="contact-info">
            {t('common.questionsContact')} <a href="mailto:pci-contact@amazon.com">pci-contact@amazon.com</a>
          </p>
          <p className="contact-info">
            {t('common.awsArtifact')} <a href="https://aws.amazon.com/artifact/" target="_blank" rel="noopener noreferrer">AWS Artifact</a>
          </p>
          <LanguageSelector />
        </div>
      </div>

      {showForgotModal && (
        <div className="modal-backdrop" onClick={() => setShowForgotModal(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h2>{t('login.forgotModal.title')}</h2>
            <p>{t('login.forgotModal.instructions')}</p>
            <ol style={{ textAlign: 'left', margin: '1rem 0', paddingLeft: '1.5rem' }}>
              <li>{t('login.forgotModal.step1')}</li>
              <li><Trans i18nKey="login.forgotModal.step2" /></li>
              <li><Trans i18nKey="login.forgotModal.step3" /></li>
            </ol>
            <a
              href="/auth/idprism/login"
              className="btn-primary"
              style={{ display: 'block', textAlign: 'center', width: '100%', marginTop: '1rem' }}
            >
              {t('common.continue')}
            </a>
            <button
              type="button"
              onClick={() => setShowForgotModal(false)}
              className="link-button"
              style={{ marginTop: '0.75rem' }}
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
